import React, { useEffect, useLayoutEffect, useState } from "react";
import ReactDOM from "react-dom";
import Separator from "./Separator";
import { StyledLabel, StyledTextContainer, StyledValue, StyledValueListItem, StyledTooltipContent, StyledFocus, } from "./ValueListItem.style";
import useUniqueId from "../../hooks/useUniqueId";
import useIdAttrs from "../../hooks/useIdAttrs";
import WithTooltip from "../with-tooltip/WithTooltip";
import ContextActionMenu from "../menu/ContextActionMenu";
import useRuler from "../../hooks/useRuler";
import LinesEllipsis from "react-lines-ellipsis";
import { SmallStatusRed, SmallStatusYellow } from "@nexthink/apollo-tokens/lib/images/react";
import { lineClamp } from "../../utils/StyleUtils";
import { dataAttribute } from "./ValueList";
import { useValueListItemOverflow } from "./ValueListContext";
import Box from "../box/Box";
export const ResponsiveLinesEllipsis = (props) => {
    const { ref } = useRuler();
    return (React.createElement("div", { ref: ref },
        React.createElement(LinesEllipsis, Object.assign({}, props))));
};
const prefixId = "nx-value-list-item-";
export const labelColIndex = 1;
const separatorColIndex = 2;
export const valueColIndex = 3;
const tryToFixOverflow = (id, labelOverflow, valueOverflow) => {
    const valueItemEl = document.getElementById(id);
    const elements = [];
    if (labelOverflow) {
        elements.push(valueItemEl === null || valueItemEl === void 0 ? void 0 : valueItemEl.querySelector(`[role=gridcell][aria-colindex='${labelColIndex}']`));
    }
    if (valueOverflow) {
        elements.push(valueItemEl === null || valueItemEl === void 0 ? void 0 : valueItemEl.querySelector(`[role=gridcell][aria-colindex='${valueColIndex}']`));
    }
    elements.forEach((el) => {
        Object.assign(el.style, lineClamp(3));
        el.setAttribute(dataAttribute, "true");
    });
};
const ValueListItem = ({ id: defaultId, "data-insights": insightsAttr, label, value, valueStatus, labelMaxWidth, valueMaxWidth, actionsRenderer, labelTooltip, tooltipMaxWidth, }) => {
    const id = useUniqueId(prefixId, defaultId);
    const idAttrs = useIdAttrs({ id, insightsAttr });
    const [isLabelEllipsed, setIsLabelEllipsed] = useState(false);
    const [isValueEllipsed, setIsValueEllipsed] = useState(false);
    const itemLabelId = idAttrs("-label");
    const valueId = idAttrs("-value");
    const { labelOverflow, valueOverflow } = useValueListItemOverflow(id);
    useLayoutEffect(() => {
        var _a, _b, _c;
        let iconWrapper;
        if (valueStatus) {
            iconWrapper = document.createElement("span");
            const icon = valueStatus === "poor" ? (React.createElement(SmallStatusRed, { "aria-label": "Value status indicator icon: poor" })) : (React.createElement(SmallStatusYellow, { "aria-label": "Value status indicator icon: average" }));
            // keep deprecated rendering until we drop support for React 17 or implement an alternative
            // eslint-disable-next-line react/no-deprecated
            ReactDOM.render(icon, iconWrapper);
            const valueItem = (_a = document.getElementById(`${id}-value`)) === null || _a === void 0 ? void 0 : _a.firstChild;
            (_c = (_b = valueItem === null || valueItem === void 0 ? void 0 : valueItem.firstChild) === null || _b === void 0 ? void 0 : _b.firstChild) === null || _c === void 0 ? void 0 : _c.before(iconWrapper);
        }
        return () => {
            if (iconWrapper) {
                iconWrapper.remove();
                // eslint-disable-next-line react/no-deprecated
                ReactDOM.unmountComponentAtNode(iconWrapper);
            }
        };
    });
    useEffect(() => {
        if (labelOverflow || valueOverflow) {
            tryToFixOverflow(id, labelOverflow, valueOverflow);
        }
    }, [labelOverflow, valueOverflow]);
    const itemLabel = (React.createElement(StyledLabel, Object.assign({ variant: "body" }, itemLabelId), typeof label === "string" ? (React.createElement(ResponsiveLinesEllipsis, { text: label, maxLine: "3", onReflow: ({ clamped }) => {
            clamped && setIsLabelEllipsed(clamped);
        } })) : (React.createElement(Box, null, label))));
    const itemValue = (React.createElement(StyledValue, Object.assign({ variant: "bodyStrong" }, valueId, { withIcon: !!valueStatus }), typeof value === "string" ? (React.createElement(ResponsiveLinesEllipsis, { text: value, maxLine: "3", onReflow: ({ clamped }) => clamped && setIsValueEllipsed(clamped) })) : (React.createElement(Box, null, value))));
    return (React.createElement(StyledValueListItem, Object.assign({}, idAttrs(), { role: "row" }),
        React.createElement(ContextActionMenu, { actions: actionsRenderer },
            React.createElement(StyledTextContainer, { maxWidth: labelMaxWidth, role: "gridcell", "aria-colindex": labelColIndex }, labelTooltip || isLabelEllipsed || labelOverflow ? (React.createElement(WithTooltip, { maxWidth: tooltipMaxWidth, alignment: "bottomLeft", tooltipContents: labelTooltip || React.createElement(StyledTooltipContent, null, label) },
                React.createElement(StyledFocus, null, itemLabel))) : (React.createElement(StyledFocus, { tabIndex: 0 }, itemLabel))),
            React.createElement(Separator, Object.assign({ actionsRenderer: actionsRenderer, role: "gridcell", "aria-colindex": separatorColIndex, label: label }, idAttrs())),
            React.createElement(StyledTextContainer, { maxWidth: valueMaxWidth, role: "gridcell", "aria-labelledby": itemLabelId.id, "aria-colindex": valueColIndex }, isValueEllipsed || valueOverflow ? (React.createElement(WithTooltip, { maxWidth: tooltipMaxWidth, alignment: "bottomRight", tooltipContents: React.createElement(StyledTooltipContent, null, value) },
                React.createElement(StyledFocus, null, itemValue))) : (React.createElement(StyledFocus, { tabIndex: 0 }, itemValue))))));
};
export default ValueListItem;
//# sourceMappingURL=ValueListItem.js.map